import React, { useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ManageAccounts as ManageAccountsIcon,
  Save as SaveIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { useAdmin } from '../context/AdminContext';
import {
  ALL_MODULES,
  EMPTY_MODULES,
  MODULES,
  MODULE_LABELS,
  ROLE_LABELS,
  ROLES,
  normalizeModules,
} from '../constants/rbac';
import { uploadProfileImage } from '../utils/profileImageUpload';

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const getInitials = (nameOrEmail = '') => {
  const value = String(nameOrEmail).trim();
  if (!value) return 'US';

  const parts = value.split(' ').filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
  }

  return value.slice(0, 2).toUpperCase();
};

const countEnabledModules = (modules = {}) =>
  MODULES.filter((moduleKey) => modules[moduleKey] === true).length;

const PermissionChecklist = ({ modules, disabled, onToggleModule }) => (
  <Grid container spacing={1}>
    {MODULES.map((moduleKey) => (
      <Grid item size={{ xs: 12, sm: 6 }} key={moduleKey}>
        <FormControlLabel
          control={
            <Checkbox
              checked={modules[moduleKey] === true}
              onChange={() => onToggleModule(moduleKey)}
              disabled={disabled}
            />
          }
          label={MODULE_LABELS[moduleKey]}
        />
      </Grid>
    ))}
  </Grid>
);

const CreateUser = () => {
  const {
    user,
    dashboardUsers,
    createDashboardUser,
    saveUserPermissions,
    updateDashboardUser,
    deleteDashboardUser,
    hasAccess,
  } = useAdmin();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    password: '',
    role: ROLES.MANAGER,
    photoURL: '',
    modules: { ...EMPTY_MODULES },
  });
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    role: ROLES.MANAGER,
    photoURL: '',
    modules: { ...EMPTY_MODULES },
  });
  const [creatingUser, setCreatingUser] = useState(false);
  const [savingUserDetails, setSavingUserDetails] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);
  const [uploadingCreateImage, setUploadingCreateImage] = useState(false);
  const [uploadingEditImage, setUploadingEditImage] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const canManagePermissions = hasAccess('createUser');

  const staffUsers = useMemo(
    () =>
      dashboardUsers.filter(
        (dashboardUser) =>
          dashboardUser.role === ROLES.ADMIN ||
          dashboardUser.role === ROLES.MANAGER ||
          dashboardUser.role === ROLES.DISPATCH
      ),
    [dashboardUsers]
  );

  const normalizedEditModules = useMemo(
    () => normalizeModules(editForm.role, editForm.modules),
    [editForm.role, editForm.modules]
  );

  const normalizedCreateModules = useMemo(
    () => normalizeModules(createForm.role, createForm.modules),
    [createForm.role, createForm.modules]
  );

  const allModulesSelected = MODULES.every(
    (moduleKey) => normalizedEditModules[moduleKey] === true
  );
  const someModulesSelected = MODULES.some(
    (moduleKey) => normalizedEditModules[moduleKey] === true
  );

  const allCreateModulesSelected = MODULES.every(
    (moduleKey) => normalizedCreateModules[moduleKey] === true
  );
  const someCreateModulesSelected = MODULES.some(
    (moduleKey) => normalizedCreateModules[moduleKey] === true
  );

  const canDeleteSelectedUser =
    canManagePermissions &&
    selectedUser &&
    selectedUser.role !== ROLES.ADMIN &&
    selectedUser.id !== user?.uid;

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((currentSnackbar) => ({ ...currentSnackbar, open: false }));
  };

  const handleOpenCreateDialog = () => {
    setCreateForm({
      name: '',
      email: '',
      password: '',
      role: ROLES.MANAGER,
      photoURL: '',
      modules: { ...EMPTY_MODULES },
    });
    setDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    if (!creatingUser && !uploadingCreateImage) {
      setDialogOpen(false);
    }
  };

  const handleCreateFormChange = (event) => {
    const { name, value } = event.target;

    if (name === 'role') {
      setCreateForm((currentForm) => ({
        ...currentForm,
        role: value,
        modules: normalizeModules(value, currentForm.modules),
      }));
      return;
    }

    setCreateForm((currentForm) => ({ ...currentForm, [name]: value }));
  };

  const handleToggleCreateModule = (moduleKey) => {
    if (createForm.role === ROLES.ADMIN) {
      return;
    }

    setCreateForm((currentForm) => ({
      ...currentForm,
      modules: {
        ...currentForm.modules,
        [moduleKey]: !currentForm.modules[moduleKey],
      },
    }));
  };

  const handleToggleAllCreateModules = (checked) => {
    if (createForm.role === ROLES.ADMIN) {
      return;
    }

    setCreateForm((currentForm) => ({
      ...currentForm,
      modules: checked ? { ...ALL_MODULES } : { ...EMPTY_MODULES },
    }));
  };

  const handleUploadCreateImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploadingCreateImage(true);
      const imageUrl = await uploadProfileImage({
        file,
        folder: 'staffProfiles',
        entityId: createForm.email || createForm.name || 'new-user',
      });
      setCreateForm((currentForm) => ({ ...currentForm, photoURL: imageUrl }));
      showSnackbar('Profile image uploaded successfully.');
    } catch (error) {
      showSnackbar(error.message || 'Failed to upload profile image.', 'error');
    } finally {
      setUploadingCreateImage(false);
      event.target.value = '';
    }
  };

  const handleCreateUser = async () => {
    if (!createForm.name.trim() || !createForm.email.trim() || !createForm.password) {
      showSnackbar('Name, email, and password are required.', 'error');
      return;
    }

    if (!isValidEmail(createForm.email.trim())) {
      showSnackbar('Please enter a valid email address.', 'error');
      return;
    }

    setCreatingUser(true);
    const result = await createDashboardUser({
      ...createForm,
      photoURL: createForm.photoURL.trim(),
      modules: normalizedCreateModules,
    });
    setCreatingUser(false);

    if (!result.success) {
      showSnackbar(result.error || 'Failed to create dashboard user.', 'error');
      return;
    }

    setDialogOpen(false);
    showSnackbar(`${ROLE_LABELS[createForm.role]} user created successfully.`);
  };

  const handleOpenUserDetails = (dashboardUser) => {
    setSelectedUser(dashboardUser);
    setEditForm({
      name: dashboardUser.name || '',
      email: dashboardUser.email || '',
      role: dashboardUser.role || ROLES.MANAGER,
      photoURL: dashboardUser.photoURL || '',
      modules: normalizeModules(dashboardUser.role, dashboardUser.modules || EMPTY_MODULES),
    });
    setDetailsDialogOpen(true);
  };

  const handleCloseUserDetails = () => {
    if (savingUserDetails || uploadingEditImage || deletingUser) return;
    setDetailsDialogOpen(false);
    setSelectedUser(null);
  };

  const handleEditFieldChange = (event) => {
    const { name, value } = event.target;

    if (name === 'role') {
      setEditForm((currentForm) => ({
        ...currentForm,
        role: value,
        modules: normalizeModules(value, currentForm.modules),
      }));
      return;
    }

    setEditForm((currentForm) => ({ ...currentForm, [name]: value }));
  };

  const handleUploadEditImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !selectedUser) return;

    try {
      setUploadingEditImage(true);
      const imageUrl = await uploadProfileImage({
        file,
        folder: 'staffProfiles',
        entityId: selectedUser.id,
      });
      setEditForm((currentForm) => ({ ...currentForm, photoURL: imageUrl }));
      showSnackbar('Profile image uploaded successfully.');
    } catch (error) {
      showSnackbar(error.message || 'Failed to upload profile image.', 'error');
    } finally {
      setUploadingEditImage(false);
      event.target.value = '';
    }
  };

  const handleToggleModule = (moduleKey) => {
    if (editForm.role === ROLES.ADMIN || !canManagePermissions) {
      return;
    }

    setEditForm((currentForm) => ({
      ...currentForm,
      modules: {
        ...currentForm.modules,
        [moduleKey]: !currentForm.modules[moduleKey],
      },
    }));
  };

  const handleToggleAllModules = (checked) => {
    if (editForm.role === ROLES.ADMIN || !canManagePermissions) {
      return;
    }

    setEditForm((currentForm) => ({
      ...currentForm,
      modules: checked ? { ...ALL_MODULES } : { ...EMPTY_MODULES },
    }));
  };

  const handleSaveUserDetails = async () => {
    if (!selectedUser) {
      return;
    }

    const nextName = editForm.name.trim();
    const nextEmail = editForm.email.trim();
    const nextPhotoURL = editForm.photoURL.trim();

    if (!nextName || !nextEmail) {
      showSnackbar('Name and email are required.', 'error');
      return;
    }

    if (!isValidEmail(nextEmail)) {
      showSnackbar('Please enter a valid email address.', 'error');
      return;
    }

    setSavingUserDetails(true);

    const detailsPayload = {};
    if (nextName !== (selectedUser.name || '')) {
      detailsPayload.name = nextName;
    }
    if (nextEmail !== (selectedUser.email || '')) {
      detailsPayload.email = nextEmail;
    }
    if (nextPhotoURL !== (selectedUser.photoURL || '')) {
      detailsPayload.photoURL = nextPhotoURL;
    }

    if (Object.keys(detailsPayload).length > 0) {
      const detailsResult = await updateDashboardUser(selectedUser.id, detailsPayload);
      if (!detailsResult.success) {
        setSavingUserDetails(false);
        showSnackbar(detailsResult.error || 'Failed to update user details.', 'error');
        return;
      }
    }

    const existingModules = normalizeModules(
      selectedUser.role,
      selectedUser.modules || EMPTY_MODULES
    );
    const requestedModules = normalizeModules(
      editForm.role,
      editForm.modules || EMPTY_MODULES
    );

    const roleChanged = editForm.role !== selectedUser.role;
    const modulesChanged = MODULES.some(
      (moduleKey) => existingModules[moduleKey] !== requestedModules[moduleKey]
    );

    if (roleChanged || modulesChanged) {
      const permissionsResult = await saveUserPermissions(
        selectedUser.id,
        editForm.role,
        requestedModules
      );

      if (!permissionsResult.success) {
        setSavingUserDetails(false);
        showSnackbar(permissionsResult.error || 'Failed to update user access.', 'error');
        return;
      }
    }

    setSavingUserDetails(false);
    setDetailsDialogOpen(false);
    setSelectedUser(null);
    showSnackbar('User details and access updated successfully.');
  };

  const handleDeleteUser = async () => {
    if (!selectedUser || !canDeleteSelectedUser) {
      return;
    }

    const confirmed = window.confirm(
      `Delete ${selectedUser.name || selectedUser.email}? This removes staff access from dashboard.`
    );
    if (!confirmed) {
      return;
    }

    setDeletingUser(true);
    const result = await deleteDashboardUser(selectedUser.id);
    setDeletingUser(false);

    if (!result.success) {
      showSnackbar(result.error || 'Failed to delete staff user.', 'error');
      return;
    }

    setDetailsDialogOpen(false);
    setSelectedUser(null);
    showSnackbar('Staff user deleted successfully.');
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            User Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage staff accounts, update profile details, delete staff, and control module access.
          </Typography>
        </Box>

        {canManagePermissions && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreateDialog}>
            Add User
          </Button>
        )}
      </Box>

      {!canManagePermissions && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You can view staff users, but only admins can edit details and permissions.
        </Alert>
      )}

      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <ManageAccountsIcon sx={{ color: '#DD2424' }} />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Staff Users
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {staffUsers.length === 0 ? (
          <Grid item size={{ xs: 12 }}>
            <Card sx={{ border: '1px dashed #d9d9d9' }}>
              <CardContent>
                <Typography color="text.secondary">No staff users found.</Typography>
              </CardContent>
            </Card>
          </Grid>
        ) : (
          staffUsers.map((dashboardUser) => {
            const modules = normalizeModules(dashboardUser.role, dashboardUser.modules || EMPTY_MODULES);
            const enabledCount = countEnabledModules(modules);

            return (
              <Grid item size={{ xs: 12, sm: 6, lg: 4 }} key={dashboardUser.id}>
                <Card
                  sx={{
                    height: '100%',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    '&:hover': {
                      borderColor: '#DD2424',
                      boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <CardActionArea onClick={() => handleOpenUserDetails(dashboardUser)}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                        <Avatar src={dashboardUser.photoURL || ''} sx={{ bgcolor: '#f5f5f5' }}>
                          {getInitials(dashboardUser.name || dashboardUser.email)}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                            {dashboardUser.name || 'Staff User'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {dashboardUser.email || 'No email'}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1, mb: 1.25 }}>
                        <Chip
                          label={ROLE_LABELS[dashboardUser.role] || 'Staff'}
                          size="small"
                          color={dashboardUser.role === ROLES.ADMIN ? 'error' : 'primary'}
                        />
                        <Chip label={`${enabledCount} modules`} size="small" variant="outlined" />
                      </Box>

                      <Typography variant="caption" color="text.secondary">
                        Click card to view, edit details, manage access, or delete staff.
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })
        )}
      </Grid>

      <Dialog open={detailsDialogOpen} onClose={handleCloseUserDetails} maxWidth="md" fullWidth>
        <DialogTitle>Staff User Details</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar src={editForm.photoURL || ''} sx={{ width: 64, height: 64, bgcolor: '#f5f5f5' }}>
                {getInitials(editForm.name || editForm.email)}
              </Avatar>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  disabled={!canManagePermissions || savingUserDetails || uploadingEditImage}
                >
                  {uploadingEditImage ? 'Uploading...' : 'Upload Image'}
                  <input hidden type="file" accept="image/*" onChange={handleUploadEditImage} />
                </Button>
              </Box>
            </Box>

            <Grid container spacing={2}>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Full Name"
                  name="name"
                  value={editForm.name}
                  onChange={handleEditFieldChange}
                  fullWidth
                  disabled={!canManagePermissions || savingUserDetails}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Email"
                  name="email"
                  value={editForm.email}
                  onChange={handleEditFieldChange}
                  fullWidth
                  disabled={!canManagePermissions || savingUserDetails}
                />
              </Grid>
              <Grid item size={{ xs: 12 }}>
                <TextField
                  select
                  label="Role"
                  name="role"
                  value={editForm.role}
                  onChange={handleEditFieldChange}
                  fullWidth
                  disabled={!canManagePermissions || savingUserDetails || selectedUser?.id === user?.uid}
                  helperText={
                    selectedUser?.id === user?.uid
                      ? 'You cannot change your own role.'
                      : 'Role controls the default access scope.'
                  }
                >
                  <MenuItem value={ROLES.ADMIN}>{ROLE_LABELS[ROLES.ADMIN]}</MenuItem>
                  <MenuItem value={ROLES.MANAGER}>{ROLE_LABELS[ROLES.MANAGER]}</MenuItem>
                  <MenuItem value={ROLES.DISPATCH}>{ROLE_LABELS[ROLES.DISPATCH]}</MenuItem>
                </TextField>
              </Grid>
            </Grid>

            {editForm.role === ROLES.ADMIN && (
              <Alert severity="info">
                Admin users always have full access to all modules.
              </Alert>
            )}

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>
              Access Permissions
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={allModulesSelected}
                  indeterminate={!allModulesSelected && someModulesSelected}
                  onChange={(event) => handleToggleAllModules(event.target.checked)}
                  disabled={!canManagePermissions || editForm.role === ROLES.ADMIN || savingUserDetails}
                />
              }
              label="Select All"
            />

            <PermissionChecklist
              modules={normalizedEditModules}
              disabled={!canManagePermissions || editForm.role === ROLES.ADMIN || savingUserDetails}
              onToggleModule={handleToggleModule}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box>
            {canDeleteSelectedUser && (
              <Button
                color="error"
                variant="outlined"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteUser}
                disabled={deletingUser || savingUserDetails}
              >
                {deletingUser ? 'Deleting...' : 'Delete Staff'}
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleCloseUserDetails} disabled={savingUserDetails || deletingUser}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveUserDetails}
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={!canManagePermissions || savingUserDetails || deletingUser || uploadingEditImage}
            >
              {savingUserDetails ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogOpen} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Staff User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar src={createForm.photoURL || ''} sx={{ width: 56, height: 56, bgcolor: '#f5f5f5' }}>
                {getInitials(createForm.name || createForm.email)}
              </Avatar>
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadIcon />}
                disabled={uploadingCreateImage || creatingUser}
              >
                {uploadingCreateImage ? 'Uploading...' : 'Upload Image'}
                <input hidden type="file" accept="image/*" onChange={handleUploadCreateImage} />
              </Button>
            </Box>

            <TextField label="Full Name" name="name" value={createForm.name} onChange={handleCreateFormChange} fullWidth />
            <TextField
              label="Email Address"
              name="email"
              type="email"
              value={createForm.email}
              onChange={handleCreateFormChange}
              fullWidth
            />
            <TextField
              label="Password"
              name="password"
              type="password"
              value={createForm.password}
              onChange={handleCreateFormChange}
              fullWidth
              helperText="Minimum 6 characters"
            />
            <TextField select label="Role" name="role" value={createForm.role} onChange={handleCreateFormChange} fullWidth>
              <MenuItem value={ROLES.ADMIN}>{ROLE_LABELS[ROLES.ADMIN]}</MenuItem>
              <MenuItem value={ROLES.MANAGER}>{ROLE_LABELS[ROLES.MANAGER]}</MenuItem>
              <MenuItem value={ROLES.DISPATCH}>{ROLE_LABELS[ROLES.DISPATCH]}</MenuItem>
            </TextField>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Access Permissions
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={allCreateModulesSelected}
                  indeterminate={!allCreateModulesSelected && someCreateModulesSelected}
                  onChange={(event) => handleToggleAllCreateModules(event.target.checked)}
                  disabled={createForm.role === ROLES.ADMIN || creatingUser || uploadingCreateImage}
                />
              }
              label="Select All"
            />

            <PermissionChecklist
              modules={normalizedCreateModules}
              disabled={createForm.role === ROLES.ADMIN || creatingUser || uploadingCreateImage}
              onToggleModule={handleToggleCreateModule}
            />
            <Typography variant="body2" color="text.secondary">
              New Admin users start with full access. Manager and Dispatch users start with modules disabled until access is assigned.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog} disabled={creatingUser || uploadingCreateImage}>
            Cancel
          </Button>
          <Button onClick={handleCreateUser} variant="contained" disabled={creatingUser || uploadingCreateImage}>
            {creatingUser ? 'Creating...' : 'Add User'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={handleCloseSnackbar} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateUser;
